// Imports
import React, { useEffect, useRef, useState } from 'react';
import { Link, Params, useParams } from 'react-router-dom';
import styled from 'styled-components/macro';
import NotFound from '../../../pages/not-found';
import Content from '../../../resources/content';
import ghost from '../../../../shareables/integrations/ghost';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Helmet } from 'react-helmet-async';
import convertElementPlainQuotes from '../../../../utils/convert-element-plain-quotes';
import sizeGalleryImages from './utils/size-gallery-images';
import wrapTables, { tableWrapperClassName } from './utils/wrap-tables';
import sanitizeHTML from '../../../../shareables/front-end/utils/sanitize-html/sanitize-html';
import { useAppDispatch } from '../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../shareables/integrations/api/redux/actions';
import { GetBlogArticleResponse } from '../../../../shareables/types/api/main/blog-article/get';
import commonActions from '../../../../shareables/foundation/front-end/redux/actions';
import useCorrectChromeScrollJump from './hooks/use-correct-chrome-scroll-jump';
import LoadingImage from '../../../resources/loading-image';
import { PostOrPage } from '@tryghost/content-api';
import prerenderData from '../../../../utils/prerender-data';
import downstepHeadings from './utils/downstep-headings';
import correctBlogLinks from './utils/correct-blog-links';


// Styled components
const MetaWrapper = styled.div`
	margin-top: 3.75rem;
	margin-right: auto;
	margin-left: auto;
	max-width: 50rem;
`;

const MetaSkeletonWrapper = styled.div`
	width: 13rem;
`;

const AuthorNameSkeletonWrapper = styled.div`
	width: 4rem;
`;

const TagLink = styled.div`
	a {
		color: hsl(206, 69%, 35%);
		text-decoration: none;
	}
	font-size: 1rem;
	line-height: 1;
	font-weight: 500;
`;

const Title = styled.h1`
	margin-top: 0.75rem;
	margin-bottom: 0.75rem;
	font-size: 2.5rem;
	line-height: 1;
	color: hsl(206, 25%, 20%);
`;

const DateWrapper = styled.span`
	font-weight: 400;
	font-size: 1rem;
	line-height: 1.2;
	color: hsl(206, 25%, 20%);
	
	strong {
		font-weight: 500;
	}
`;

const FeatureImage = styled(LoadingImage)`
	width: 100%;
	height: auto;
	border-radius: 0.625rem;
	margin-bottom: 3.75rem;
	
	&:is(div) {
		aspect-ratio: 2 / 1;
	}
`;

const FeatureImageSkeletonWrapper = styled.div`
	width: 100%;
	aspect-ratio: 2 / 1;
`;

const ContentWrapper = styled.div`
	margin-top: 3.75rem;
	margin-left: auto;
	margin-right: auto;
	display: flex;
	max-width: 62.5rem;
	flex-direction: column;
	align-items: center;
`;

const Authors = styled.div`
	display: flex;
	margin-top: 1.5rem;
	margin-right: auto;
	gap: 0.375rem;
	align-items: center;
`;

const AuthorImageContainer = styled.div`
	margin-left: 0.5rem;
	display: flex;
`;

const AuthorNameRoleContainer = styled.div`
	width: 100%;
`;

const AuthorName = styled.div`
	line-height: 1 !important;
	font-size: 1.0938rem !important;
	margin-bottom: 0.25rem !important;
	color: hsl(206, 25%, 20%);
`;

const AuthorRole = styled.div`
	line-height: 1 !important;
	font-size: 0.9375rem !important;
	color: hsl(206, 15%, 40%);
`;

const ProfileImage = styled(LoadingImage)`
	border: 0.125rem solid hsl(0, 0%, 100%);
	border-radius: 62.4375rem;
	width: 2.5rem;
	height: 2.5rem;
	margin-left: -0.5rem;
`;

const ProfileImageSkeleton = styled(Skeleton)`
	margin-left: -0.5rem;
`;

const PostContent = styled.div`
	max-width: 50rem;
	
	img {
		width: 100%;
		height: auto;
		margin-bottom: 1rem;
		border-radius: 0.3125rem;
		border: 0.0625rem solid hsl(206, 60%, 94%);
	}
	
	ul,
	ol {
		padding-left: 1.9rem;
	}
	
	p,
	ul,
	ol {
		font-weight: 400;
		font-size: 1.125rem;
		line-height: 1.4;
		
		li {
			margin-bottom: 0.5rem;
		}
		
		a > u {
			text-decoration: inherit;
		}
		
		&:not(:last-child) {
			margin-bottom: 1rem;
		}
		
		a {
			color: inherit;
			text-decoration: underline;
			cursor: pointer;
			
			&:hover {
				text-decoration: none;
			}
		}
		
		b {
			font-weight: 600;
		}
	}
	
	p + ul {
		margin-block-start: -0.25rem;
		padding-inline-start: 2.5rem;
		
		&:not(:last-child) {
			margin-bottom: 1rem;
		}
	}
	
	h2,
	h3,
	h4,
	h5,
	h6 {
		font-weight: 600;
		margin-top: 2rem;
		margin-bottom: 0.5rem;
	}
	
	h3 {
		font-size: 1.25rem;
	}
	
	strong {
		font-weight: 600;
	}
	
	figcaption {
		margin-top: -0.75rem;
		margin-bottom: 1rem;
		opacity: 0.5;
		text-align: center;
		
		p {
			font-size: 0.875rem;
		}
	}
	
	.${tableWrapperClassName} {
		width: 100%;
		max-width: 100%;
		margin: 1.5rem 0;
		overflow: auto;
		overflow-y: hidden;
	}
	
	table {
		width: 100%;
		border-collapse: collapse;
		border-spacing: 0;
		font-size: 1.125rem;
		line-height: 1.2;
	}
	
	th,
	td {
		padding: 0.45rem 0.5rem;
		text-align: left;
	}
	
	th {
		border-bottom: 0.0625rem solid hsl(206, 69%, 87%);
		font-weight: 600;
		padding-top: 0;
		padding-bottom: 0.625rem;
	}
	
	tbody > tr:first-of-type td {
		padding-top: 0.625rem;
	}
	
	tbody > tr:nth-of-type(even) {
		background: hsl(206, 30%, 98%);
	}
	
	.kg-gallery-container {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
		margin-bottom: 1rem;
		
		@media (max-width: 48rem) {
			gap: 0.375rem;
		}
	}
	
	.kg-gallery-row {
		display: flex;
		gap: 0.75rem;
		
		@media (max-width: 48rem) {
			gap: 0.375rem;
		}
	}
	
	.kg-gallery-image {
		flex: 1 1 0.125rem;
		min-width: 0;
		
		img {
			width: 100%;
			height: auto;
			margin-bottom: 0;
		}
	}
`;

const ContentSkeletonWrapper = styled.div`
	width: min(50rem, 95%);
	margin-top: 3.75rem;
`;


// Define the accepted props
interface Props {
	storybookPost?: GetBlogArticleResponse;
}


// Define what a post looks like
interface PostWithOpenGraphData extends GetBlogArticleResponse {
	ogTitle?: string;
	ogDescription?: string;
	ogImage?: string;
}


// Shape a raw Ghost post into the structure this component renders
const shapeGhostPost = (response: PostOrPage): PostWithOpenGraphData => ({
	authors:
		response.authors?.map((author) => ({
			id: author.id,
			name: author.name ?? undefined,
			profileImage: author.profile_image ?? undefined,
			role: author.bio ?? undefined,
		})) ?? [],
	tags: response.tags ?? [],
	content: response.html ?? undefined,
	featureImage: response.feature_image ?? undefined,
	published: response.published_at ? new Date(response.published_at) : undefined,
	title: response.title,
	ogTitle: response.og_title ?? undefined,
	ogDescription: response.og_description ?? response.custom_excerpt ?? response.excerpt ?? undefined,
	ogImage: response.og_image ?? response.feature_image ?? undefined,
});


// Function component
const Post: React.FC<Props> = ({ storybookPost }) => {
	// Use React Router functionality
	const { postIdentifier, postUUID } = useParams<Params>();
	
	
	// Initialize
	const prerenderKey = postIdentifier ? `blog:post:${postIdentifier}` : '';
	
	
	// Use state
	const [post, setPost] = useState<PostWithOpenGraphData | undefined>(() => {
		if (storybookPost) {
			return storybookPost;
		}
		
		const prerendered = postIdentifier ? prerenderData.read<PostOrPage>(prerenderKey) : undefined;
		
		return prerendered ? shapeGhostPost(prerendered) : undefined;
	});
	
	const [postNotFound, setPostNotFound] = useState(false);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use ref
	const postRef = useRef<HTMLDivElement | null>(null);
	const postContentRef = useRef<HTMLDivElement | null>(null);
	
	
	// Correct Chrome's buggy scroll restoration jump on refresh
	// LATER: Remove when Chrome no longer causes a scroll jump on refresh
	useCorrectChromeScrollJump(post !== undefined || postNotFound);
	
	
	// Load post on mount
	useEffect(() => {
		if (postIdentifier) {
			// Restore the prerendered post then revalidate in the background
			return prerenderData.revalidate<PostOrPage>(
				prerenderKey,
				(data) => {
					setPost(shapeGhostPost(data));
					setPostNotFound(false);
				},
				() => ghost.posts.read({ slug: postIdentifier }, { include: ['authors', 'tags'] }),
				() => setPostNotFound(true)
			);
		} else if (postUUID) {
			void dispatch(
				apiActions.call<undefined, undefined, GetBlogArticleResponse>({
					action: 'GET',
					uri: `/blog-article/${postUUID}`,
					handleErrorsAutomatically: false,
					completion: (json) => {
						// Handle article found
						if (json.status === 'success') {
							setPost(json.response);
							return;
						}
						
						
						// Handle article not found
						if (json.code === 'ENDPOINT_ERROR' && json.message.includes('Blog article not found')) {
							setPostNotFound(true);
							return;
						}
						
						
						// Handle all other errors
						dispatch(
							commonActions.showPageMessage({
								color: 'danger',
								title: 'Oh snap!',
								message: json.message,
							})
						);
					},
				})
			);
		}
	}, [dispatch, postUUID, postIdentifier, prerenderKey]);
	
	
	// Enhance the rendered post
	useEffect(() => {
		if (postRef.current) {
			convertElementPlainQuotes(postRef.current);
			sizeGalleryImages(postRef.current);
			wrapTables(postRef.current);
		}
		
		if (postContentRef.current) {
			downstepHeadings(postContentRef.current);
			correctBlogLinks(postContentRef.current);
		}
	}, [post]);
	
	
	// Handle post not found
	if (postNotFound) {
		return <NotFound />;
	}
	
	
	// Return JSX
	return (
		<Content>
			{post?.title && (
				<Helmet title={`${post.title} // Blog`}>
					<meta property='og:type' content='article' />
					<meta property='og:title' content={post.ogTitle ?? post.title} />
					<meta property='og:url' content={window.location.href} />
					{post.ogDescription && <meta property='og:description' content={post.ogDescription} />}
					{post.ogImage && <meta property='og:image' content={post.ogImage} />}
				</Helmet>
			)}
			<div ref={postRef}>
				<MetaWrapper>
					<TagLink>
						{post ? (
							post.tags
								?.map<React.ReactNode>((tag) => (
									<Link key={tag.id} to={`../tag/${tag.slug}`}>
										{tag.name}
									</Link>
								))
								.reduce((prev, curr) => (prev === null ? curr : [prev, ', ', curr]), null)
						) : (
							<MetaSkeletonWrapper>
								<Skeleton />
							</MetaSkeletonWrapper>
						)}
					</TagLink>
					
					<Title>{post ? post.title : <Skeleton />}</Title>
					<DateWrapper>
						{post ? (
							<React.Fragment>
								Published{' '}
								<strong>
									{(post.published ?? new Date(Date.now())).toLocaleDateString(undefined, {
										month: 'long',
										day: 'numeric',
										year: 'numeric',
									})}
								</strong>
							</React.Fragment>
						) : (
							<Skeleton width='13rem' />
						)}
					</DateWrapper>
					
					<Authors>
						<AuthorImageContainer>
							{post ? (
								post.authors?.map((author) => (
									<ProfileImage key={author.id} src={author.profileImage} alt={author.name} />
								))
							) : (
								<ProfileImageSkeleton width='2.4rem' height='2.4rem' borderRadius='62.4375rem' />
							)}
						</AuthorImageContainer>
						<AuthorNameRoleContainer>
							<AuthorName>
								{post ? (
									post.authors?.map((author) => author.name).join(', ')
								) : (
									<AuthorNameSkeletonWrapper>
										<Skeleton width='100%' height='1.0938rem' />
									</AuthorNameSkeletonWrapper>
								)}
							</AuthorName>
							<AuthorRole>
								{post ? (
									post.authors?.map((author) => author.role).join(', ')
								) : (
									<MetaSkeletonWrapper>
										<Skeleton width='100%' height='1.0938rem' />
									</MetaSkeletonWrapper>
								)}
							</AuthorRole>
						</AuthorNameRoleContainer>
					</Authors>
				</MetaWrapper>
				
				<ContentWrapper>
					{post?.featureImage ? (
						<FeatureImage src={post.featureImage} alt={post.title} />
					) : (
						<FeatureImageSkeletonWrapper>
							<Skeleton width='100%' height='100%' />
						</FeatureImageSkeletonWrapper>
					)}
					
					{post ? (
						<PostContent
							ref={postContentRef}
							dangerouslySetInnerHTML={{
								__html: sanitizeHTML(post.content ?? ''),
							}}
						/>
					) : (
						<ContentSkeletonWrapper>
							<Skeleton count={10} height='1.575rem' width='100%' />
						</ContentSkeletonWrapper>
					)}
				</ContentWrapper>
			</div>
		</Content>
	);
};

export default Post;
